import type { DialogPrimitiveOverlayProps, DialogPrimitiveProps } from './DialogPrimitive.tsx';
import { ScrollArea } from '@base-ui/react/scroll-area';
import { clsx } from 'clsx';
import React, { Suspense } from 'react';
import { PiXBold } from 'react-icons/pi';
import { Bleed } from '../Bleed/index.ts';
import { Button } from '../Button/index.ts';
import { ErrorMessage } from '../ErrorMessage/index.ts';
import { Heading } from '../Heading/index.ts';
import { LoadingDots } from '../LoadingDots/index.ts';
import { Text } from '../Text/index.ts';
import {
  DialogPrimitive,
  DialogPrimitiveClose,
  DialogPrimitiveContent,
  DialogPrimitiveDescription,
  DialogPrimitiveOverlay,
  DialogPrimitivePortal,
  DialogPrimitiveTitle,
  DialogPrimitiveTrigger
} from './DialogPrimitive.tsx';

export type DialogControlProps = Pick<
  DialogPrimitiveProps,
  'onAnimationEnd' | 'onOpenChange' | 'open'
>;

export type DialogProps = DialogControlProps &
  React.PropsWithChildren<{
    disablePointerDismissal?: boolean;
    loading?: boolean;
    overlayColor?: DialogPrimitiveOverlayProps['color'];
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    showCloseButton?: boolean;
    showSwipeHandle?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl';
  }>;

function Dialog({
  children,
  disablePointerDismissal = false,
  loading = false,
  onAnimationEnd,
  onOpenChange,
  open = false,
  overlayColor,
  scrollContainerRef,
  showCloseButton = false,
  showSwipeHandle,
  size = 'md'
}: DialogProps) {
  const resolvedShowSwipeHandle = showSwipeHandle ?? !disablePointerDismissal;

  return (
    <DialogPrimitive
      disablePointerDismissal={disablePointerDismissal}
      onAnimationEnd={onAnimationEnd}
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogPrimitivePortal>
        <DialogPrimitiveOverlay color={overlayColor} />
        <DialogPrimitiveContent
          animation="responsive"
          className={clsx(
            'inset-x-0 bottom-0 mt-24 flex max-h-[95%] w-full max-w-full flex-col overflow-hidden rounded-t-[10px] bg-white',
            'sm:top-1/2 sm:right-auto sm:bottom-auto sm:left-1/2 sm:mt-0 sm:max-h-[90%] sm:min-h-0 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:shadow-lg',
            size === 'sm' && 'sm:max-w-xs',
            size === 'md' && 'sm:max-w-md',
            size === 'lg' && 'sm:max-w-2xl',
            size === 'xl' && 'sm:max-w-4xl'
          )}
          disableSwipeDismissal={disablePointerDismissal}
          swipeHandle={
            resolvedShowSwipeHandle ? (
              <div
                aria-hidden
                className="mx-auto mt-4 h-1.5 w-12 flex-none rounded-full bg-gray-300 sm:hidden"
              />
            ) : undefined
          }
        >
          <DialogPrimitiveTitle className="hidden" />
          <DialogPrimitiveDescription className="hidden" />

          <Suspense fallback={<LoadingDots className="py-24" />}>
            {loading && (
              <>
                <LoadingDots className="py-24" />
              </>
            )}

            {!loading && (
              <>
                <ScrollArea.Root className="mt-4 flex min-h-0 w-full min-w-0 flex-auto grow overflow-hidden rounded-t-[10px] sm:mt-5">
                  <ScrollArea.Viewport
                    ref={scrollContainerRef}
                    className={clsx(
                      'min-h-0 w-full min-w-0 flex-auto overflow-x-hidden overflow-y-auto overscroll-contain outline-none',
                      'px-4 pb-4 [--gutter-x:--spacing(4)] [--gutter-y:--spacing(4)]',
                      'sm:px-6 sm:pb-6 sm:[--gutter-x:--spacing(6)] sm:[--gutter-y:--spacing(6)]'
                    )}
                  >
                    <ScrollArea.Content className="w-full max-w-full" style={{ minWidth: 0 }}>
                      {children}
                    </ScrollArea.Content>
                  </ScrollArea.Viewport>

                  <ScrollArea.Scrollbar
                    orientation="vertical"
                    className="z-1 mr-0.5 hidden w-1.5 justify-center rounded bg-transparent opacity-0 transition-opacity delay-300 duration-150 ease-out data-hovering:opacity-100 data-hovering:delay-0 data-scrolling:opacity-100 data-scrolling:delay-0 motion-reduce:transition-none sm:flex"
                  >
                    <ScrollArea.Thumb className="w-full rounded-full bg-black/30" />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>

                {showCloseButton && (
                  <div
                    className={clsx(
                      'absolute top-4 right-2.5 z-40 sm:right-4',
                      resolvedShowSwipeHandle && 'hidden sm:block'
                    )}
                  >
                    <DialogPrimitiveClose asChild>
                      <Button icon={<PiXBold />} rounded variant="subtle" />
                    </DialogPrimitiveClose>
                  </div>
                )}
              </>
            )}
          </Suspense>
        </DialogPrimitiveContent>
      </DialogPrimitivePortal>
    </DialogPrimitive>
  );
}

function DialogHeader({
  children,
  className,
  errorMessage,
  sticky = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  errorMessage?: React.ReactNode;
  sticky?: boolean;
}) {
  return (
    <Bleed
      {...props}
      className={clsx(
        'z-1 flex flex-col gap-y-1.5 bg-white pb-(--gutter-y)',
        errorMessage ? 'mb-(--gutter-y)' : 'mb-px',
        sticky && 'sticky -top-(--gutter-y)',
        className
      )}
      horizontal
      top
    >
      {children}

      {!!errorMessage && (
        <Bleed
          className="mt-[calc(var(--gutter-y)/2)] bg-red-50 px-(--gutter-x) py-4"
          bottom
          horizontal
          padded={false}
        >
          <ErrorMessage message={errorMessage} plain />
        </Bleed>
      )}
    </Bleed>
  );
}

function DialogFooter({
  actions,
  children,
  className,
  sticky = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  actions?: boolean;
  sticky?: boolean;
}) {
  return (
    <Bleed
      {...props}
      className={clsx(
        'mt-1 bg-white pt-5',
        actions && 'mt-2 flex flex-col gap-1.5 sm:flex-row-reverse sm:justify-start sm:gap-2',
        sticky && 'sticky -bottom-(--gutter-y)',
        className
      )}
      bottom
      horizontal
    >
      <div className="absolute inset-x-0 top-0 bottom-full hidden h-4 -translate-y-full bg-linear-to-t from-black/7.5 to-transparent in-data-overflow-y-end:block" />

      {children}
    </Bleed>
  );
}

function DialogTitle({
  asChild,
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof DialogPrimitiveTitle>) {
  return (
    <DialogPrimitiveTitle asChild {...props}>
      {!asChild ? (
        <Heading className="text-center sm:text-left" level={3} thin>
          {children}
        </Heading>
      ) : (
        children
      )}
    </DialogPrimitiveTitle>
  );
}

function DialogDescription({
  children,
  ...props
}: React.ComponentPropsWithRef<typeof DialogPrimitiveDescription>) {
  return (
    <DialogPrimitiveDescription {...props}>
      <Text subdued>{children}</Text>
    </DialogPrimitiveDescription>
  );
}

const DialogCloseButton = DialogPrimitiveClose;

const DialogTrigger = DialogPrimitiveTrigger;

export {
  Dialog,
  DialogCloseButton,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  DialogTitle
};
